/**
 * Cart Recommendations Component
 * Mostra recomendações automáticas baseadas nos produtos do carrinho
 * Usa a Shopify Product Recommendations API
 */

class CartRecommendations extends HTMLElement {
  constructor() {
    super();
    this.recommendationsUrl = this.dataset.url;
    this.limit = parseInt(this.dataset.limit) || 4;
    this.container = this.querySelector('[data-recommendations-container]');
    this.wrapper = this.querySelector('[data-recommendations-wrapper]');
    this.loadingEl = this.querySelector('.m-cart-recommendations__loading');
    this.sliderControls = this.querySelector('[data-slider-controls]');
    this.slider = null;
    this.currentProductId = null; // Guarda o produto base atual
    this.isRefreshing = false; // Flag para evitar múltiplas chamadas
    this.refreshTimeout = null;
    
    this.init();
    this.setupDrawerListener();
    this.setupCartUpdateListener();
  }

  setupDrawerListener() {
    // Atualiza o slider quando o drawer terminar de abrir
    const cartDrawer = document.getElementById('MinimogCartDrawer');
    if (cartDrawer) {
      cartDrawer.addEventListener('transitionend', () => {
        if (this.slider && cartDrawer.classList.contains('m-cart-drawer--active')) {
          setTimeout(() => {
            this.slider.update();
          }, 50);
        }
      });
    }
  }

  setupCartUpdateListener() {
    // Escuta eventos de atualização do carrinho
    document.addEventListener('cart:updated', () => this.refresh());
    
    // Usa o sistema de eventos nativo do Minimog (mais confiável)
    if (window.MinimogEvents && window.MinimogTheme?.pubSubEvents?.cartUpdate) {
      window.MinimogEvents.subscribe(window.MinimogTheme.pubSubEvents.cartUpdate, () => {
        // Delay para esperar o DOM ser atualizado
        setTimeout(() => this.refresh(), 100);
      });
    }
    
    // Também escuta quando o drawer abre (para recarregar se necessário)
    if (window.MinimogEvents && window.MinimogTheme?.pubSubEvents?.openCartDrawer) {
      window.MinimogEvents.subscribe(window.MinimogTheme.pubSubEvents.openCartDrawer, () => {
        setTimeout(() => this.refresh(), 150);
      });
    }
  }

  refresh() {
    // Debounce para evitar múltiplas chamadas
    if (this.refreshTimeout) {
      clearTimeout(this.refreshTimeout);
    }
    
    this.refreshTimeout = setTimeout(() => {
      this.doRefresh();
    }, 200);
  }

  async doRefresh() {
    // Evita múltiplas chamadas simultâneas
    if (this.isRefreshing) return;
    this.isRefreshing = true;
    
    try {
      const cart = await this.getCart();
      
      // Se carrinho vazio, esconde
      if (!cart || !cart.items || cart.items.length === 0) {
        this.currentProductId = null;
        this.hide();
        return;
      }

      // Verifica se o produto base mudou
      const newProductId = cart.items[0].product_id;
      
      // Se já tem recomendações do mesmo produto, apenas atualiza visibilidade
      if (this.currentProductId === newProductId && this.getAttribute('data-loaded') === 'true') {
        this.show();
        return;
      }

      // Produto base mudou, recarrega recomendações
      console.log('Cart Recommendations: Produto base mudou, recarregando...');
      this.currentProductId = newProductId;
      this.reset();
      await this.loadRecommendations(cart);
      
    } catch (error) {
      console.error('Cart Recommendations: Erro ao atualizar:', error);
    } finally {
      this.isRefreshing = false;
    }
  }

  reset() {
    // Reseta o estado do componente
    this.removeAttribute('data-loaded');
    this.style.display = '';
    
    // Destrói o slider se existir
    if (this.slider) {
      this.slider.destroy(true, true);
      this.slider = null;
    }
    
    // Limpa o conteúdo
    if (this.wrapper) {
      this.wrapper.innerHTML = '';
    }
    
    // Mostra loading
    if (this.loadingEl) {
      this.loadingEl.style.display = '';
    }
    
    // Esconde controles
    if (this.sliderControls) {
      this.sliderControls.classList.add('m:hidden');
    }
  }

  show() {
    this.style.display = '';
  }

  async init() {
    try {
      const cart = await this.getCart();
      
      if (!cart || !cart.items || cart.items.length === 0) {
        console.log('Cart Recommendations: Carrinho vazio, ocultando recomendações');
        this.hide();
        return;
      }

      this.currentProductId = cart.items[0].product_id;
      await this.loadRecommendations(cart);
    } catch (error) {
      console.error('Erro ao carregar recomendações:', error);
      this.hide();
    }
  }

  async loadRecommendations(cart) {
    const baseProduct = cart.items[0];
    console.log(`Cart Recommendations: Buscando recomendações para produto ID ${baseProduct.product_id}`);
    
    // Busca recomendações da API do Shopify
    const recommendations = await this.fetchRecommendations(baseProduct.product_id);
    
    if (recommendations && recommendations.length > 0) {
      console.log(`Cart Recommendations: ${recommendations.length} produto(s) recomendado(s) encontrado(s)`);
      this.show();
      this.renderRecommendations(recommendations, cart.items);
    } else {
      console.log('Cart Recommendations: Nenhuma recomendação encontrada, ocultando seção');
      this.hide();
    }
  }

  async getCart() {
    try {
      const response = await fetch('/cart.js');
      return await response.json();
    } catch (error) {
      console.error('Erro ao buscar carrinho:', error);
      return null;
    }
  }

  async fetchRecommendations(productId) {
    try {
      // URL da API de Product Recommendations do Shopify (formato JSON)
      // O data-url já deve incluir .json (ex: /recommendations/products.json)
      const url = `${this.recommendationsUrl}?product_id=${productId}&limit=${this.limit}&intent=related`;
      console.log(`Cart Recommendations: Fazendo request para ${url}`);
      
      const response = await fetch(url);
      
      if (!response.ok) {
        console.warn(`Cart Recommendations: API retornou status ${response.status} - ${response.statusText}`);
        return [];
      }

      const data = await response.json();
      console.log('Cart Recommendations: Resposta da API:', data);
      
      // A API retorna { products: [...] }
      return data.products || [];
    } catch (error) {
      console.error('Cart Recommendations: Erro ao buscar recomendações:', error);
      return [];
    }
  }

  renderRecommendations(products, cartItems) {
    // Filtra produtos que já estão no carrinho
    const cartProductIds = cartItems.map(item => item.product_id);
    const filteredProducts = products.filter(product => !cartProductIds.includes(product.id));

    if (filteredProducts.length === 0) {
      this.hide();
      return;
    }

    // Limita ao número configurado
    const productsToShow = filteredProducts.slice(0, this.limit);
    
    // Renderiza os cards dentro de swiper-slide
    this.wrapper.innerHTML = productsToShow
      .map(product => {
        const cardHtml = this.createProductCard(product);
        return cardHtml ? `<div class="swiper-slide">${cardHtml}</div>` : '';
      })
      .filter(Boolean)
      .join('');
    
    // Marca como carregado
    this.setAttribute('data-loaded', 'true');
    
    // Inicializa event listeners para os botões
    this.initializeButtons();
    
    // Inicializa o slider se houver mais de 1 produto
    if (productsToShow.length > 1) {
      this.initSlider();
    }
  }

  createProductCard(product) {
    // Pega a primeira variante disponível
    const availableVariant = product.variants ? product.variants.find(v => v.available) : null;
    
    if (!availableVariant) {
      return ''; // Não mostra produtos esgotados
    }
    
    const hasMultipleVariants = product.variants && product.variants.length > 1;
    const priceFormatted = this.formatPrice(availableVariant.price);
    const installmentHtml = this.formatInstallment(availableVariant.price);
    const imageUrl = product.featured_image || '';

    // Ícone de + branco
    const plusIcon = `
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M10 4V16M4 10H16" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    `;

    let buttonHtml = '';
    
    if (hasMultipleVariants) {
      // Se tem múltiplas variantes, usa o botão de quickview
      buttonHtml = `
        <button
          type="button"
          class="m-reco-card__add-btn m-product-quickview-button"
          data-product-handle="${product.handle}"
          data-product-id="${product.id}"
          data-product-url="${product.url}"
          aria-label="Selecionar opções"
        >
          ${plusIcon}
        </button>
      `;
    } else {
      // Se tem apenas uma variante, adiciona direto ao carrinho
      buttonHtml = `
        <button
          type="button"
          class="m-reco-card__add-btn"
          data-add-to-cart
          data-variant-id="${availableVariant.id}"
          aria-label="Adicionar ao carrinho"
        >
          ${plusIcon}
        </button>
      `;
    }

    return `
      <div class="m-reco-card">
        <a href="${product.url}" class="m-reco-card__image">
          ${imageUrl ? `<img src="${imageUrl}" alt="${this.escapeHtml(product.title)}" loading="lazy">` : ''}
        </a>
        <div class="m-reco-card__info">
          <h4 class="m-reco-card__title">
            <a href="${product.url}">${this.escapeHtml(product.title)}</a>
          </h4>
          <div class="m-reco-card__price">${priceFormatted}</div>
          ${installmentHtml ? `<div class="m-reco-card__installment">${installmentHtml}</div>` : ''}
        </div>
        <div class="m-reco-card__button">
          ${buttonHtml}
        </div>
      </div>
    `;
  }

  initializeButtons() {
    // Inicializa botões de adicionar ao carrinho
    const addToCartButtons = this.container.querySelectorAll('[data-add-to-cart]');
    addToCartButtons.forEach(button => {
      button.addEventListener('click', (e) => this.handleAddToCart(e, button));
    });
  }

  async handleAddToCart(event, button) {
    event.preventDefault();
    
    const variantId = button.dataset.variantId;
    if (!variantId) return;

    // Adiciona estado de loading
    button.classList.add('m-spinner-loading');
    button.disabled = true;

    try {
      const formData = {
        items: [{
          id: variantId,
          quantity: 1
        }]
      };

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        // Dispara evento para atualizar o carrinho
        const cartDrawer = document.getElementById('MinimogCartDrawer');
        if (cartDrawer) {
          cartDrawer.onCartDrawerUpdate();
        }
        
        // Mostra feedback visual
        const texts = window.MinimogStrings || {};
        const addedText = texts.added || 'Adicionado!';
        const addToCartText = texts.addToCart || 'Adicionar ao carrinho';
        
        button.textContent = addedText;
        setTimeout(() => {
          button.textContent = addToCartText;
          button.classList.remove('m-spinner-loading');
          button.disabled = false;
        }, 2000);
      } else {
        throw new Error('Erro ao adicionar ao carrinho');
      }
    } catch (error) {
      console.error('Erro ao adicionar produto:', error);
      const addToCartText = (window.MinimogStrings || {}).addToCart || 'Adicionar ao carrinho';
      button.textContent = 'Erro';
      button.classList.remove('m-spinner-loading');
      setTimeout(() => {
        button.textContent = addToCartText;
        button.disabled = false;
      }, 2000);
    }
  }

  formatPrice(price) {
    // Formata o preço usando o formato do Shopify
    const priceInCents = parseInt(price);
    const priceInDollars = (priceInCents / 100).toFixed(2);
    
    if (window.MinimogSettings && window.MinimogSettings.money_format) {
      // Substitui todos os formatos possíveis do Shopify
      return window.MinimogSettings.money_format
        .replace('{{amount}}', priceInDollars)
        .replace('{{amount_with_comma_separator}}', priceInDollars.replace('.', ','))
        .replace('{{amount_no_decimals}}', Math.round(priceInCents / 100))
        .replace('{{amount_with_space_separator}}', priceInDollars.replace('.', ' '));
    }
    
    // Fallback para formato brasileiro
    return (priceInCents / 100).toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    });
  }

  formatInstallment(price) {
    // Pega as configurações de parcelamento do tema
    const settings = window.MinimogSettings || {};
    const showInstallments = settings.show_installments !== false;
    
    if (!showInstallments) return '';
    
    const installments = parseInt(settings.modo_parcelamento) || 12;
    const priceInCents = parseInt(price);
    const installmentValue = priceInCents / installments / 100;
    
    const formattedValue = installmentValue.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    
    return `ou ${installments}x de R$ ${formattedValue} sem juros`;
  }

  escapeHtml(text) {
    const map = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;'
    };
    return text.replace(/[&<>"']/g, m => map[m]);
  }

  initSlider() {
    // Verifica se o Swiper está disponível
    if (!window.MinimogLibs || !window.MinimogLibs.Swiper) {
      console.warn('Cart Recommendations: Swiper library not loaded');
      return;
    }

    const prevBtn = this.querySelector('.m-slider-controls__button-prev');
    const nextBtn = this.querySelector('.m-slider-controls__button-next');

    try {
      // Mesmo formato do megamenu - 1 produto por vez em todas as telas
      this.slider = new MinimogLibs.Swiper(this.container, {
        slidesPerView: 1,
        loop: false,
        autoplay: false
      });

      // Conecta os botões (mesmo do megamenu)
      if (this.slider) {
        prevBtn && prevBtn.addEventListener('click', () => this.slider.slidePrev());
        nextBtn && nextBtn.addEventListener('click', () => this.slider.slideNext());
        
        // Mostra os controles
        if (this.sliderControls) {
          this.sliderControls.classList.remove('m:hidden');
        }

        // Update slider após um delay para calcular tamanhos corretos (igual megamenu)
        setTimeout(() => {
          this.slider && this.slider.update();
        }, 100);
      }

      console.log('Cart Recommendations: Slider inicializado com sucesso');
    } catch (error) {
      console.error('Cart Recommendations: Erro ao inicializar slider:', error);
    }
  }

  hide() {
    // Esconde completamente a seção se não houver recomendações
    this.style.display = 'none';
  }
  
  showEmpty() {
    // Mostra mensagem de "sem recomendações"
    const emptyEl = this.querySelector('.m-cart-recommendations__empty');
    if (emptyEl) {
      emptyEl.style.display = 'block';
    }
    // Esconde o loading
    if (this.loadingEl) {
      this.loadingEl.style.display = 'none';
    }
    this.setAttribute('data-loaded', 'true');
  }
}

// Registra o custom element
if (!customElements.get('cart-recommendations')) {
  customElements.define('cart-recommendations', CartRecommendations);
}

